import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/api";
import { useClient } from "@/hooks/useClient";
import { Activation, Audience, Insight, InsightMeta } from "@/types";

type InsightData = { audience: Audience; insight: Insight };

/**
 * Keeps cached audiences, lists and insights in step with changes made in the
 * app, so pages show the change without refetching.
 */
export const useAudienceCache = () => {
  const queryClient = useQueryClient();
  const client = useClient();

  return useMemo(() => {
    // Applies a change to one audience wherever it's cached
    const updateAudience = (audienceId: string, update: (audience: Audience) => Audience) => {
      const apply = (audience: Audience) => audience.audience_id === audienceId ? update(audience) : audience;

      queryClient.setQueriesData<Audience[]>({ queryKey: queryKeys.audiences() }, (list: Audience[] | undefined) => list?.map(apply));
      queryClient.setQueriesData<Audience>({ queryKey: queryKeys.audience(audienceId) }, (audience: Audience | undefined) => audience && apply(audience));
      queryClient.setQueriesData<InsightData>({ queryKey: queryKeys.insight(audienceId) }, (data: InsightData | undefined) =>
        data && { ...data, audience: apply(data.audience) });
    };

    return {
      rename: (audienceId: string, audienceName: string, projectName: string | null) =>
        updateAudience(audienceId, (audience) => ({
          ...audience,
          audience_name: audienceName.trim(),
          project_name: projectName ? projectName.trim() : projectName,
        })),

      share: (audienceId: string, userIds: number[]) =>
        updateAudience(audienceId, (audience) => ({ ...audience, shared_with: userIds })),

      /** Drops a deleted audience straight away, then refetches the lists */
      remove: (audienceId: string) => {
        queryClient.setQueriesData<Audience[]>({ queryKey: queryKeys.audiences() }, (list: Audience[] | undefined) =>
          list?.filter((audience) => audience.audience_id !== audienceId));
        queryClient.removeQueries({ queryKey: queryKeys.audience(audienceId) });
        queryClient.removeQueries({ queryKey: queryKeys.insight(audienceId) });
        return queryClient.invalidateQueries({ queryKey: queryKeys.audiences() });
      },

      /** After a save or clone: the lists and the saved audience are refetched when next used */
      saved: (audienceId?: string) => {
        audienceId && queryClient.invalidateQueries({ queryKey: queryKeys.audience(audienceId) });
        return queryClient.invalidateQueries({ queryKey: queryKeys.audiences() });
      },

      /** A freshly calculated insight, so the insight page doesn't fetch it again */
      setInsight: (audienceId: string, data: InsightData) =>
        queryClient.setQueryData(queryKeys.insightDetail(audienceId, client, false), data),

      setInsightMeta: (audienceId: string, meta: InsightMeta[]) =>
        queryClient.setQueriesData<InsightData>({ queryKey: queryKeys.insight(audienceId) }, (data: InsightData | undefined) =>
          data && { ...data, insight: { ...data.insight, meta } }),
    };
  }, [client, queryClient]);
};

/** Keeps cached activations in step with changes made in the app */
export const useActivationCache = () => {
  const queryClient = useQueryClient();
  const client = useClient();

  return useMemo(() => ({
    setList: (activations: Activation[]) =>
      queryClient.setQueryData(queryKeys.activations(client), activations),

    share: (campaignId: string, userIds: number[]) =>
      queryClient.setQueryData<Activation[]>(queryKeys.activations(client), (list: Activation[] | undefined) =>
        list?.map((activation) => activation.campaign_id === campaignId
          ? { ...activation, shared_with: userIds }
          : activation)),

    remove: (campaignId: string, activations?: Activation[]) => {
      if (activations) queryClient.setQueryData(queryKeys.activations(client), activations);
      queryClient.removeQueries({ queryKey: queryKeys.activation(campaignId) });
    },

    /** After saving a recipe, so the activation and recipe are refetched when next used */
    recipeSaved: (campaignId: string) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.activation(campaignId) }),
  }), [client, queryClient]);
};
